"use client";

// The side panel (design spec §2, §8.1): 360 px beside main, `--card`, a hairline on the left,
// underline tabs in a 40 px header with the close button, a scrolling body. Full-screen between
// the top bar and the bottom bar on phones.
//
// CHAT-PANEL (founder review 2026-10-28): optionally resizable from its left edge (`resize`: the
// app owns the width, its bounds and where it is kept). Only a tab's body scrolls: the header and
// the frame never do. A `flush` tab (a thread: its own scrolling list and pinned composer) fills
// the body edge to edge and scrolls inside itself.
import { XIcon } from "lucide-react";
import * as React from "react";
import { cn } from "../cn";
import { Button } from "./button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./tabs";

type SidePanelTab = {
  value: string;
  label: string;
  content: React.ReactNode;
  /** The tab scrolls inside itself and fills the body (no padding, no outer scroll). */
  flush?: boolean;
};

type SidePanelResize = {
  /** The panel's width in px. */
  width: number;
  min: number;
  max: number;
  /** A new width (unclamped) while dragging (`done` false) or once settled (`done` true). */
  onResize: (px: number, done: boolean) => void;
  /** Double-click on the edge: back to the default width. */
  onReset: () => void;
  /** Arrow-key step in px (default 24). */
  step?: number;
};

function ResizeEdge({ resize }: { resize: SidePanelResize }) {
  const drag = React.useRef<{ x: number; w: number } | null>(null);
  const step = resize.step ?? 24;
  // The panel grows leftwards: moving the pointer left widens it.
  const at = (clientX: number) => (drag.current ? drag.current.w + drag.current.x - clientX : 0);
  const end = (clientX: number) => {
    if (!drag.current) return;
    resize.onResize(at(clientX), true);
    drag.current = null;
  };
  return (
    // biome-ignore lint/a11y/useSemanticElements: an <hr> cannot take focus or a value; the WAI-ARIA window splitter is a focusable separator.
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize side panel"
      aria-valuemin={resize.min}
      aria-valuemax={resize.max}
      aria-valuenow={resize.width}
      tabIndex={0}
      title="Drag to resize · double-click to reset"
      data-slot="side-panel-resize"
      onPointerDown={(event) => {
        event.preventDefault();
        event.currentTarget.setPointerCapture?.(event.pointerId);
        drag.current = { x: event.clientX, w: resize.width };
      }}
      onPointerMove={(event) => {
        if (drag.current) resize.onResize(at(event.clientX), false);
      }}
      onPointerUp={(event) => end(event.clientX)}
      onPointerCancel={(event) => end(event.clientX)}
      onDoubleClick={resize.onReset}
      onKeyDown={(event) => {
        const to: Record<string, number> = {
          ArrowLeft: resize.width + step,
          ArrowRight: resize.width - step,
          Home: resize.min,
          End: resize.max,
        };
        const next = to[event.key];
        if (next === undefined) return;
        event.preventDefault();
        event.stopPropagation();
        resize.onResize(next, true);
      }}
      className={cn(
        "group/resize absolute inset-y-0 -left-1 z-10 w-2 cursor-col-resize touch-none outline-none max-md:hidden",
        // The hairline turns --primary on hover, while dragged and when focused; a small grip
        // shows at its middle on hover.
        "after:absolute after:inset-y-0 after:left-[3px] after:w-0.5 after:bg-transparent after:transition-colors after:duration-(--dur-fast) hover:after:bg-primary/50 focus-visible:after:bg-primary active:after:bg-primary",
        "before:absolute before:top-1/2 before:left-px before:z-10 before:h-8 before:w-1.5 before:-translate-y-1/2 before:rounded-full before:border before:border-border-strong before:bg-card before:opacity-0 before:transition-opacity before:duration-(--dur-fast) hover:before:opacity-100 focus-visible:before:opacity-100 active:before:opacity-100",
      )}
    />
  );
}

function SidePanel({
  id,
  "aria-label": label = "Side panel",
  tabs,
  value,
  onValueChange,
  onClose,
  resize,
  className,
}: {
  id?: string;
  "aria-label"?: string;
  tabs: readonly SidePanelTab[];
  value: string;
  onValueChange: (value: string) => void;
  onClose: () => void;
  /** Resizable from the left edge (wide screens only; a phone shows the panel full-screen). */
  resize?: SidePanelResize;
  className?: string;
}) {
  return (
    <aside
      id={id}
      aria-label={label}
      data-slot="side-panel"
      // The live width (the token holds the default): the one runtime variable.
      style={
        resize ? ({ "--side-panel-w": `${resize.width}px` } as React.CSSProperties) : undefined
      }
      className={cn(
        "relative flex h-full min-h-0 shrink-0 flex-col border-l border-border bg-card text-card-foreground",
        resize ? "w-(--side-panel-w)" : "w-panel",
        "max-md:fixed max-md:inset-x-0 max-md:top-(--shell-top,var(--topbar-h)) max-md:bottom-14 max-md:z-drawer max-md:w-full max-md:border-l-0",
        className,
      )}
    >
      <Tabs
        value={value}
        onValueChange={(v) => onValueChange(String(v))}
        className="flex min-h-0 flex-1 flex-col gap-0"
      >
        <div
          data-slot="side-panel-header"
          className="flex h-10 shrink-0 items-center gap-2 overflow-hidden border-b border-border pr-2 pl-4"
        >
          {/* CHAT-PANEL: the line list scrolls sideways by default, which makes it a scroll box
              on both axes; the active tab's underline (1 px below the list) then gave the header
              a vertical scrollbar beside the close button. Here the list fills the header's
              height and never scrolls. */}
          <TabsList
            variant="line"
            aria-label="Panels"
            className="min-w-0 flex-1 overflow-visible border-b-0 group-data-horizontal/tabs:h-full group-data-horizontal/tabs:border-b-0"
          >
            {tabs.map((t) => (
              <TabsTrigger key={t.value} value={t.value}>
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
          <Button
            variant="ghost"
            size="icon-sm"
            className="pointer-coarse:size-11"
            aria-label={`Close ${label.toLowerCase()}`}
            onClick={onClose}
          >
            <XIcon aria-hidden="true" />
          </Button>
        </div>
        {tabs.map((t) => (
          <TabsContent
            key={t.value}
            value={t.value}
            data-flush={t.flush ? "" : undefined}
            className={
              t.flush
                ? "flex min-h-0 flex-col overflow-hidden"
                : "min-h-0 overflow-y-auto p-4 scrollbar-gutter-stable"
            }
          >
            {t.content}
          </TabsContent>
        ))}
      </Tabs>
      {resize ? <ResizeEdge resize={resize} /> : null}
    </aside>
  );
}

export { SidePanel, type SidePanelResize, type SidePanelTab };
